import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
    useInitiateNewDDASignatureMutation,
    useReinitiateDDASignatureMutation,
    useUpdateBankingStatementMutation,
} from 'api/main';
import { bankingStatementStatus, paymentMethods } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import RibReplaceDialog from '../RibReplaceDialog';
import { ActionButton, ConfirmDialog } from 'components';
import { Box, Typography } from '@mui/material';

const actionButtonStyle = {
    borderRadius: '5px',
    padding: '2px 5px',
    marginLeft: '20px',
};

const getPaymentMethodDialogContent = (paymentMethod, hasDDA, investmentFund) => {
    return paymentMethod === paymentMethods.DIRECT_DEBIT ? (
        <Typography>{texts.CHANGE_TO_CREDIT_TRANSFER}</Typography>
    ) : investmentFund?.collectionEndDate &&
      moment(investmentFund.collectionEndDate).isBefore(moment(), 'd') ? (
        <Typography>{texts.CHANGE_TO_DIRECT_DEBIT_AFTER_COLLECTION_DATE}</Typography>
    ) : (
        <>
            <Typography>{texts.CHANGE_TO_DIRECT_DEBIT}</Typography>
            <Typography>{hasDDA ? texts.ALREADY_SIGNED_DDA : texts.NEED_TO_SIGN_DDA}</Typography>
        </>
    );
};

const RIBReplacementActions = ({
    doc,
    bankingStatement,
    investmentFund,
    hasDDA,
    canChangePaymentMethod,
}) => {
    const [ribDialogOpen, setRibDialogOpen] = useState(false);
    const [paymentMethodDialogOpen, setPaymentMethodDialogOpen] = useState(false);

    const [
        reinitiateDDASignature,
        { isLoading: signatureLoader, isSuccess: signatureSuccess, data: signatureData },
    ] = useReinitiateDDASignatureMutation();

    const [updateBankingStatement, { isLoading: bankingLoader, isSuccess: bankingSuccess }] =
        useUpdateBankingStatementMutation();

    const [
        initiateNewDDA,
        { isLoading: ddaLoader, isSuccess: ddaSuccess, data: ddaResponse, reset: resetDDA },
    ] = useInitiateNewDDASignatureMutation();

    const handleInitiateAPSignature = useCallback(() => {
        reinitiateDDASignature(bankingStatement?.id);
    }, [reinitiateDDASignature, bankingStatement]);

    const handleChangePaymentMethod = useCallback(() => {
        if (ddaSuccess && ddaResponse?.signerUrl) {
            window.open(ddaResponse.signerUrl);
            setPaymentMethodDialogOpen(false);
            return;
        }
        const newPaymentMethod =
            bankingStatement?.paymentMethod === paymentMethods.DIRECT_DEBIT
                ? paymentMethods.CREDIT_TRANSFER
                : paymentMethods.DIRECT_DEBIT;
        if (
            !hasDDA &&
            bankingStatement?.paymentMethod === paymentMethods.CREDIT_TRANSFER &&
            (!investmentFund?.collectionEndDate ||
                moment(investmentFund.collectionEndDate).isAfter(moment(), 'd'))
        ) {
            initiateNewDDA(bankingStatement?.id);
        } else {
            updateBankingStatement({ id: bankingStatement?.id, paymentMethod: newPaymentMethod });
        }
    }, [
        bankingStatement,
        ddaResponse,
        ddaSuccess,
        hasDDA,
        initiateNewDDA,
        updateBankingStatement,
        investmentFund,
    ]);

    useEffect(() => {
        if (signatureSuccess && signatureData?.signerUrl) {
            window.open(signatureData.signerUrl);
        }
    }, [signatureData, signatureSuccess]);

    useEffect(() => {
        if (bankingSuccess) {
            setPaymentMethodDialogOpen(false);
        }
    }, [bankingSuccess]);

    useEffect(() => {
        if (ddaSuccess && ddaResponse?.signerUrl) {
            setTimeout(() => {
                window.open(ddaResponse.signerUrl);
                resetDDA();
                setPaymentMethodDialogOpen(false);
            }, 1000);
        }
    }, [ddaResponse, ddaSuccess, resetDDA]);

    return (
        <React.Fragment>
            <Box>
                {bankingStatement?.status === bankingStatementStatus.PENDING_VERIFICATION && (
                    <Typography
                        sx={{
                            marginLeft: '20px',
                            fontSize: 15,
                            fontStyle: 'italic',
                            lineHeight: 1.6,
                            '@media (max-width:720px)': {
                                margin: 0,
                            },
                        }}
                    >
                        {texts.IBAN_VERIFICATION_PENDING}
                    </Typography>
                )}
                {bankingStatement?.status === bankingStatementStatus.PENDING_VALIDATION && (
                    <Typography
                        sx={{
                            marginLeft: '20px',
                            fontSize: 15,
                            fontStyle: 'italic',
                            lineHeight: 1.6,
                            '@media (max-width:720px)': {
                                margin: 0,
                            },
                        }}
                    >
                        {texts.IBAN_VALIDATION_PENDING}
                    </Typography>
                )}
                {bankingStatement?.status === bankingStatementStatus.PENDING_SIGNATURE && (
                    <ActionButton
                        variant="outlined"
                        sx={actionButtonStyle}
                        onClick={handleInitiateAPSignature}
                        loading={signatureLoader}
                    >
                        {texts.SIGN_DIRECT_DEBIT_AUTHORIZATION}
                    </ActionButton>
                )}
                {bankingStatement?.status === bankingStatementStatus.OK && (
                    <ActionButton
                        variant="outlined"
                        sx={actionButtonStyle}
                        onClick={() => setRibDialogOpen(true)}
                    >
                        {texts.REPLACE}
                    </ActionButton>
                )}
                <RibReplaceDialog
                    open={ribDialogOpen}
                    doc={doc}
                    onClose={() => setRibDialogOpen(false)}
                    bankingStatement={bankingStatement}
                />
            </Box>
            <Box display="flex" alignItems="center" flexBasis="100%">
                <Typography
                    sx={{
                        marginLeft: '20px',
                        fontSize: 15,
                        fontStyle: 'italic',
                        lineHeight: 1.6,
                        '@media (max-width:720px)': {
                            margin: 0,
                        },
                    }}
                >
                    {bankingStatement?.paymentMethod === paymentMethods.DIRECT_DEBIT
                        ? texts.YOU_CHOSE_DIRECT_DEBIT
                        : texts.YOU_CHOSE_CREDIT_TRANSFER}
                </Typography>
                {bankingStatement?.status === bankingStatementStatus.OK &&
                    canChangePaymentMethod && (
                        <ActionButton
                            variant="outlined"
                            sx={actionButtonStyle}
                            onClick={() => setPaymentMethodDialogOpen(true)}
                        >
                            {texts.EDIT}
                        </ActionButton>
                    )}
                <ConfirmDialog
                    open={paymentMethodDialogOpen}
                    title={texts.PAYMENT_METHOD_DIALOG_TITLE}
                    handleClose={() => setPaymentMethodDialogOpen(false)}
                    handleConfirm={handleChangePaymentMethod}
                    loading={bankingLoader || ddaLoader}
                >
                    {getPaymentMethodDialogContent(
                        bankingStatement?.paymentMethod,
                        hasDDA,
                        investmentFund
                    )}
                </ConfirmDialog>
            </Box>
        </React.Fragment>
    );
};

RIBReplacementActions.propTypes = {
    doc: PropTypes.object.isRequired,
    bankingStatement: PropTypes.object,
    investmentFund: PropTypes.object,
    hasDDA: PropTypes.bool,
    canChangePaymentMethod: PropTypes.bool,
};

export default RIBReplacementActions;
